import { ReactNode, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import {
  AppBar, Toolbar, IconButton, Drawer, Box, List, ListItemButton, ListItemIcon,
  ListItemText, Typography, Avatar, Menu, MenuItem, Badge, Chip, Divider,
  useMediaQuery, useTheme, Tooltip, Fab,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/MenuRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import { NAV } from "../config/nav";
import { useTenant } from "../context/TenantContext";
import { useAccess } from "../context/AccessContext";
import { useUIState } from "../context/UIStateContext";
import { displayName } from "../lib/identity";
import { BrandMark } from "./BrandMark";
import { SophiaPanel } from "./SophiaPanel";

const DRAWER_W = 240;
const DRAWER_W_MINI = 68;
const SOPHIA_W = 320;

function initials(name?: string): string {
  if (!name) return "??";
  const parts = name.split(/[@\s._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function Shell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant, tenants, isInternal, setTenant } = useTenant();
  const { can, temModulo } = useAccess();
  const podeSophia = can("sophia");
  const { sidebarCollapsed, toggleSidebar, sophiaOpen: sophiaAberta, toggleSophia, setSophiaOpen } = useUIState();
  const sophiaOpen = sophiaAberta && podeSophia;
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  // Sophia inline starts at md (>=900px) — matches sidebar breakpoint so
  // the layout is symmetric: menu on the left, Sophia on the right, both
  // permanent and scroll-fixed.
  const isLgUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [tenantAnchor, setTenantAnchor] = useState<HTMLElement | null>(null);
  const [avatarAnchor, setAvatarAnchor] = useState<HTMLElement | null>(null);

  const username = displayName(auth.user?.profile) || "user";
  const currentItem = NAV.flatMap((s) => s.items).find((i) => i.path === location.pathname);

  const showMini = isMdUp && sidebarCollapsed;
  const sidebarW = !isMdUp ? DRAWER_W : (sidebarCollapsed ? DRAWER_W_MINI : DRAWER_W);
  const sophiaInline = isLgUp && sophiaOpen;

  function go(path: string) { navigate(path); setMobileDrawerOpen(false); }

  const sidebarContent = (
    <Box
      sx={{
        width: sidebarW,
        height: "100%",
        bgcolor: "primary.dark",
        color: "common.white",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: theme.transitions.create("width", { duration: theme.transitions.duration.shorter }),
      }}
      data-testid="sidebar"
    >
      <Box sx={{
        p: showMini ? 1.5 : 2, borderBottom: "1px solid rgba(255,255,255,0.08)",
        display: "flex", alignItems: "center", justifyContent: showMini ? "center" : "flex-start", minHeight: 60,
      }}>
        {showMini
          ? <BrandMark size={30} layout="simbolo" variant="light" />
          // Vertical, nao horizontal: a peca horizontal tem quase
          // 8:1 e nao cabe nos 240px da barra — vazava e cortava o
          // texto no proprio limite da lateral.
          : <BrandMark size={54} layout="vertical" variant="light" />}
      </Box>
      <Box sx={{ p: 1, flex: 1, overflowY: "auto" }}>
        {NAV.map((section) => {
          // Secao de modulo so existe para quem contratou. Sem isto, um cliente
          // de vendas ve "Relatorios" e "Notificacoes" — que so existem para o
          // plano de compra — e abre as duas vazias.
          if (section.modulo && !temModulo(section.modulo)) return null;
          // Secao cujos itens o usuario todo nao pode ver nao vira titulo solto.
          const visiveis = section.items.filter((i) => !i.area || can(i.area));
          if (visiveis.length === 0) return null;
          return (
          <Box key={section.id} sx={{ mb: 1.5 }}>
            {section.label && !showMini && (
              <Typography variant="overline" sx={{ px: 1.5, color: "rgba(255,255,255,0.4)", display: "block", mb: 0.5 }}>
                {section.label}
              </Typography>
            )}
            <List disablePadding>
              {visiveis.map((item) => {
                const active = location.pathname === item.path;
                const Icon = item.Icon;
                const button = (
                  <ListItemButton
                    key={item.id}
                    selected={active}
                    onClick={() => go(item.path)}
                    component={NavLink as any}
                    to={item.path}
                    data-testid={`nav-${item.id}`}
                    sx={{
                      color: active ? "common.white" : "rgba(255,255,255,0.75)",
                      minHeight: 42,
                      px: showMini ? 1 : 1.5,
                      justifyContent: showMini ? "center" : "flex-start",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.06)", color: "common.white" },
                      "&.Mui-selected": {
                        bgcolor: "rgba(252,171,16,0.15)",
                        color: "common.white",
                        borderLeft: showMini ? "none" : "3px solid #fcab10",
                        pl: showMini ? 1 : 1.25,
                        "&:hover": { bgcolor: "rgba(252,171,16,0.22)" },
                        "& .MuiListItemIcon-root": { color: "#fcab10" },
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: "rgba(255,255,255,0.65)",
                      minWidth: showMini ? 0 : 36,
                      mr: showMini ? 0 : 0,
                      justifyContent: "center",
                    }}>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    {!showMini && (
                      <>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 600 : 400 }}
                        />
                        {item.badge && (
                          <Chip label={item.badge} size="small" color="secondary" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                        )}
                      </>
                    )}
                  </ListItemButton>
                );
                return showMini ? (
                  <Tooltip key={item.id} title={item.label} placement="right">{button}</Tooltip>
                ) : button;
              })}
            </List>
          </Box>
          );
        })}
      </Box>
      {isMdUp && (
        <Box sx={{ p: 0.75, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "center" }}>
          <Tooltip title={sidebarCollapsed ? "Expandir menu" : "Colapsar menu"} placement="right">
            <IconButton size="small" onClick={toggleSidebar} sx={{ color: "rgba(255,255,255,0.75)", "&:hover": { color: "common.white" } }}>
              {sidebarCollapsed ? <ChevronRightRoundedIcon /> : <ChevronLeftRoundedIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{
      display: "flex",
      height: "100vh",        // fixed to viewport — no page-level scroll
      overflow: "hidden",     // scroll happens only inside <main>
      bgcolor: "background.default",
    }}>
      {/* Sidebar */}
      {isMdUp ? (
        <Drawer
          variant="permanent"
          sx={{ width: sidebarW, flexShrink: 0, "& .MuiDrawer-paper": { width: sidebarW, borderRight: "none", bgcolor: "transparent", overflow: "hidden" } }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Drawer variant="temporary" open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)} ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: DRAWER_W, borderRight: "none", bgcolor: "transparent" } }}>
          {sidebarContent}
        </Drawer>
      )}

      {/* Center + right */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <AppBar position="sticky" elevation={0}>
          <Toolbar sx={{ gap: 1, minHeight: { xs: 56, md: 64 }, px: { xs: 1, md: 3 } }}>
            {!isMdUp && (
              <IconButton
                edge="start"
                onClick={() => setMobileDrawerOpen(true)}
                aria-label="Abrir menu"
                data-testid="hamburger"
                sx={{ width: 44, height: 44 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: "text.secondary", display: { xs: "none", sm: "inline" } }}>Plataforma</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", display: { xs: "none", sm: "inline" } }}>/</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {currentItem?.label ?? "Home"}
              </Typography>
            </Box>

            {/* Tenant chip: desktop e tablet inline; mobile abaixo (segunda linha) */}
            <Tooltip title={isInternal ? "Você é admin interno" : "Trocar tenant"}>
              <Chip
                icon={<BusinessRoundedIcon fontSize="small" />}
                label={tenant ?? "escolha"}
                deleteIcon={<ExpandMoreRoundedIcon />}
                onDelete={(e) => setTenantAnchor(e.currentTarget as HTMLElement)}
                onClick={(e) => setTenantAnchor(e.currentTarget as HTMLElement)}
                data-testid="tenant-switch"
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  bgcolor: "background.default", borderRadius: 2, fontWeight: 600, color: "primary.dark",
                  height: 36,
                  "& .MuiChip-icon": { color: "primary.main" },
                  "& .MuiChip-deleteIcon": { color: "text.secondary", "&:hover": { color: "primary.main" } },
                }}
              />
            </Tooltip>
            <Menu anchorEl={tenantAnchor} open={Boolean(tenantAnchor)} onClose={() => setTenantAnchor(null)} PaperProps={{ sx: { minWidth: 220, mt: 1 } }}>
              {isInternal && (
                <Box sx={{ px: 2, pb: 1, display: "flex", alignItems: "center", gap: 0.75, color: "warning.main" }}>
                  <ShieldRoundedIcon fontSize="small" />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>admin interno</Typography>
                </Box>
              )}
              {tenants.map((t) => (
                <MenuItem key={t} selected={t === tenant} onClick={() => { setTenant(t); setTenantAnchor(null); }}>{t}</MenuItem>
              ))}
            </Menu>

            {can("notificacoes") && (
            <IconButton
              onClick={() => go("/notificacoes")}
              aria-label="Notificações"
              sx={{ width: 44, height: 44, display: { xs: "none", sm: "inline-flex" } }}
            >
              <Badge color="secondary" variant="dot" overlap="circular"><NotificationsRoundedIcon /></Badge>
            </IconButton>
            )}

            {isLgUp && podeSophia && (
              <Tooltip title={sophiaOpen ? "Esconder Sophia" : "Mostrar Sophia"}>
                <IconButton
                  onClick={toggleSophia}
                  aria-label="Toggle Sophia"
                  sx={{ width: 44, height: 44, color: sophiaOpen ? "secondary.main" : "text.secondary" }}
                >
                  <SmartToyRoundedIcon />
                </IconButton>
              </Tooltip>
            )}

            <IconButton
              onClick={(e) => setAvatarAnchor(e.currentTarget)}
              data-testid="avatar-menu"
              sx={{ width: 44, height: 44 }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main", fontSize: 13, fontWeight: 600 }}>{initials(username)}</Avatar>
            </IconButton>
            <Menu anchorEl={avatarAnchor} open={Boolean(avatarAnchor)} onClose={() => setAvatarAnchor(null)} PaperProps={{ sx: { minWidth: 220, mt: 1 } }}>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{username}</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>Sessão ativa</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => { setAvatarAnchor(null); go("/perfil"); }}>
                <ListItemIcon><PersonRoundedIcon fontSize="small" /></ListItemIcon>
                Meu perfil
              </MenuItem>
              <MenuItem onClick={() => { setAvatarAnchor(null); auth.removeUser().then(() => auth.signoutRedirect({
                // o Cognito ignora post_logout_redirect_uri e so volta para o app com logout_uri
                extraQueryParams: { logout_uri: auth.settings.post_logout_redirect_uri ?? window.location.origin },
              })); }} data-testid="signout">
                <ListItemIcon><LogoutRoundedIcon fontSize="small" /></ListItemIcon>
                Sair
              </MenuItem>
            </Menu>
          </Toolbar>

          {/* Segunda linha mobile: tenant chip + notificações (evita header cheio no xs) */}
          <Box sx={{
            display: { xs: "flex", sm: "none" },
            alignItems: "center", gap: 1,
            px: 1, pb: 1,
            borderTop: 1, borderColor: "divider",
          }}>
            <Tooltip title={isInternal ? "Você é admin interno" : "Trocar tenant"}>
              <Chip
                icon={<BusinessRoundedIcon fontSize="small" />}
                label={tenant ?? "escolha"}
                deleteIcon={<ExpandMoreRoundedIcon />}
                onDelete={(e) => setTenantAnchor(e.currentTarget as HTMLElement)}
                onClick={(e) => setTenantAnchor(e.currentTarget as HTMLElement)}
                data-testid="tenant-switch-mobile"
                sx={{
                  bgcolor: "background.default", borderRadius: 2, fontWeight: 600, color: "primary.dark",
                  height: 36, flex: 1, justifyContent: "space-between",
                  "& .MuiChip-icon": { color: "primary.main" },
                  "& .MuiChip-label": { flex: 1 },
                  "& .MuiChip-deleteIcon": { color: "text.secondary" },
                }}
              />
            </Tooltip>
            {can("notificacoes") && (
            <IconButton
              onClick={() => go("/notificacoes")}
              aria-label="Notificações"
              sx={{ width: 44, height: 44 }}
            >
              <Badge color="secondary" variant="dot" overlap="circular"><NotificationsRoundedIcon /></Badge>
            </IconButton>
            )}
          </Box>
        </AppBar>

        {/* Main + inline Sophia — both constrained to viewport minus AppBar */}
        <Box sx={{ flex: 1, display: "flex", minWidth: 0, overflow: "hidden", height: "100%" }}>
          <Box
            component="main"
            sx={{
              flex: 1,
              p: { xs: 2, sm: 3, md: 3 },
              overflowY: "auto",   // ONLY this element scrolls
              minWidth: 0,
              height: "100%",
            }}
          >
            <Box sx={{ maxWidth: 1200, mx: "auto", display: "flex", flexDirection: "column", gap: 2.5 }}>
              {children}
            </Box>
          </Box>
          {sophiaInline && (
            <Box sx={{
              width: SOPHIA_W,
              flexShrink: 0,
              borderLeft: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              display: "flex",
              flexDirection: "column",
              height: "100%",       // stays exactly viewport height
              overflow: "hidden",   // no vertical growth
            }}>
              <SophiaPanel variant="inline" onClose={() => setSophiaOpen(false)} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Mobile / small-screen Sophia via drawer, and toggle FAB */}
      {!isLgUp && podeSophia && (
        <>
          <Drawer
            anchor="right" open={sophiaOpen} onClose={() => setSophiaOpen(false)}
            PaperProps={{ sx: { width: { xs: "100%", sm: 380 } } }}
          >
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <SophiaPanel variant="drawer" onClose={() => setSophiaOpen(false)} />
            </Box>
          </Drawer>
          <Tooltip title="Sophia" placement="left">
            <Fab
              color="primary" aria-label="Sophia" data-testid="sophia-fab"
              onClick={() => setSophiaOpen(true)}
              sx={{
                position: "fixed", bottom: 20, right: 20, zIndex: (t) => t.zIndex.speedDial,
                background: "linear-gradient(135deg, #021a2c 0%, #2c4055 100%)",
                color: "#fcab10",
                "&:hover": { background: "linear-gradient(135deg, #021a2c 0%, #576b82 100%)", color: "#ffdd50" },
              }}
            >
              <SmartToyRoundedIcon />
            </Fab>
          </Tooltip>
        </>
      )}
    </Box>
  );
}
